import React, { useContext } from 'react';
import { Box, Newline, Text } from 'ink';
import type { GitLabRemoteInfo, AgenticChatAccessStatus } from '../../types';
import { EnvironmentContext } from '../environment_context';
import { useTheme } from '../theme';
import { AppHeader } from './AppHeader';
import { Link } from './Link';
import { Spinner } from './Spinner';

export interface InteractiveModeHeaderProps {
  username?: string;
  credentialSource?: string;
  gitlabRemoteInfo: GitLabRemoteInfo;
  cwd?: string;
  agenticChatAccess?: AgenticChatAccessStatus;
  columns: number;
}

const InitializingDots: React.FC = () => {
  const { tokens } = useTheme();
  return (
    <Box>
      <Text color={tokens.text.subtle}>Initializing </Text>
      <Spinner spinner="line" />
    </Box>
  );
};

const UserStatus: React.FC<{ username?: string; credentialSource?: string }> = ({
  username,
  credentialSource,
}) => {
  const { tokens } = useTheme();
  return (
    <Text>
      <Text bold>User: </Text>
      {username || '...'}
      {username && credentialSource && (
        <Text color={tokens.text.subtle}> ({credentialSource})</Text>
      )}
    </Text>
  );
};

const DuoAccessStatus: React.FC<{ accessStatus?: AgenticChatAccessStatus }> = ({
  accessStatus,
}) => {
  const { tokens } = useTheme();
  if (!accessStatus || accessStatus.status === 'checking') {
    return (
      <Text>
        <Text bold>GitLab Duo access: ...</Text>
      </Text>
    );
  }

  if (accessStatus.status === 'available') {
    // TEMPORARY back-compat shim (removable): access granted via a <19.2
    // experimental-gate path; see `packages/cli/src/experimental_feature_gate.ts`.
    if (accessStatus.experimental) {
      return (
        <Text>
          <Text bold>GitLab Duo access: </Text>
          <Text color={tokens.text.warning}>Beta</Text>
        </Text>
      );
    }

    return (
      <Text>
        <Text bold>GitLab Duo access: </Text>
        <Text color={tokens.text.success}>✓ Available</Text>
      </Text>
    );
  }

  // unavailable
  return (
    <Box flexDirection="column">
      <Text>
        <Text bold>GitLab Duo access: </Text>
        <Text color={tokens.text.danger}>✗ Unavailable</Text>
      </Text>
      {accessStatus.reason && (
        <Box marginLeft={2}>
          <Text color={tokens.text.warning}>{accessStatus.reason}</Text>
        </Box>
      )}
    </Box>
  );
};

const ProjectStatus: React.FC<{ gitlabRemoteInfo: GitLabRemoteInfo }> = ({ gitlabRemoteInfo }) => {
  const { tokens } = useTheme();
  switch (gitlabRemoteInfo.status) {
    case 'connected': {
      const gitlabRemoteUrl = new URL(
        gitlabRemoteInfo.gitlabPath,
        `https://${gitlabRemoteInfo.gitlabHost}`,
      );
      const urlString = gitlabRemoteUrl.toString();
      return (
        <Box flexDirection="row" flexWrap="wrap">
          <Text bold>Project: </Text>
          <Link url={urlString}>{urlString}</Link>
        </Box>
      );
    }
    case 'error': {
      return <Text color={tokens.text.danger}>{`${gitlabRemoteInfo.errorMessage}`}</Text>;
    }
    default:
      return (
        <Text>
          <Text bold>Project: </Text>
          {'...'}
        </Text>
      );
  }
};

export const InteractiveModeHeader: React.FC<InteractiveModeHeaderProps> = ({
  username,
  credentialSource,
  gitlabRemoteInfo,
  cwd,
  agenticChatAccess,
  columns,
}) => {
  const envInfo = useContext(EnvironmentContext);
  const { tokens } = useTheme();

  const isInitializing =
    !username ||
    gitlabRemoteInfo.status === 'not-checked' ||
    !agenticChatAccess ||
    agenticChatAccess.status === 'checking';

  return (
    <Box flexDirection="column">
      <AppHeader columns={columns}>
        <Box flexDirection="column" paddingLeft={1}>
          <Text>
            <Text bold>GitLab Duo CLI</Text>
            {` v${envInfo.duoCliVersion}`}
          </Text>
          <Text> </Text>
          <UserStatus username={username} credentialSource={credentialSource} />
          <DuoAccessStatus accessStatus={agenticChatAccess} />
          <ProjectStatus gitlabRemoteInfo={gitlabRemoteInfo} />
          {cwd && (
            <Text>
              <Text bold>cwd: </Text>
              <Text color={tokens.text.subtle}>{cwd}</Text>
            </Text>
          )}
        </Box>
      </AppHeader>
      <Newline />
      {isInitializing && <InitializingDots />}
    </Box>
  );
};
